import { ellipsize, fitTextBlock, placeLines, type MeasureFn, type PlacedLine, type Region, type TextBlock } from "./layout";

/**
 * Pure layouts for the interior boards (founder feedback 2026-10-09, item 3): the store marketing
 * board (TV or whiteboard) and the Real Estate office listings board. Text is owner-written plain
 * text or server-formatted labels; it is only ever measured and drawn as canvas text.
 */
export interface BoardText {
  title: string;
  body: string;
}

export interface ListingLineText {
  address: string;
  priceLabel: string;
}

export const NO_MESSAGE_TITLE = "No message yet";
export const NO_MESSAGE_BODY = "The owner has not posted anything on this board.";

export interface MessageBoardLayout {
  /** Screen area inside the bezel / board edge. */
  screen: Region;
  title: TextBlock;
  titleBox: Region;
  titleLines: PlacedLine[];
  body: TextBlock;
  bodyBox: Region;
  bodyLines: PlacedLine[];
  empty: boolean;
}

/** Title (large, <= 2 lines) above a wrapped body (<= 6 lines), both contain-fit in the screen. */
export function layoutMessageBoard(board: BoardText | null, measureBold: MeasureFn, measureRegular: MeasureFn, width: number, height: number): MessageBoardLayout {
  const empty = !board || !board.title.trim();
  const title = empty ? NO_MESSAGE_TITLE : board!.title;
  const body = empty ? NO_MESSAGE_BODY : board!.body;
  const pad = Math.round(Math.min(width, height) * 0.08);
  const screen: Region = { x: pad, y: pad, w: width - pad * 2, h: height - pad * 2 };
  const inner = Math.round(screen.h * 0.06);
  const titleH = Math.round(screen.h * (body.trim() ? 0.36 : 0.8));
  const titleBox: Region = { x: screen.x + inner, y: screen.y + inner, w: screen.w - inner * 2, h: titleH };
  const titleBlock = fitTextBlock(measureBold, title, titleBox, { maxPx: Math.min(titleH * 0.7, 120), minPx: 18, maxLines: 2 });
  const bodyBox: Region = { x: screen.x + inner, y: titleBox.y + titleBox.h + inner * 0.5, w: screen.w - inner * 2, h: screen.y + screen.h - inner - (titleBox.y + titleBox.h + inner * 0.5) };
  const bodyBlock = body.trim()
    ? fitTextBlock(measureRegular, body, bodyBox, { maxPx: Math.min(titleBlock.fontPx * 0.68, 66), minPx: 14, maxLines: 6, lineHeightFactor: 1.22 })
    : { lines: [], fontPx: 14, lineHeight: 0, width: 0, height: 0, truncated: false };
  return {
    screen,
    title: titleBlock,
    titleBox,
    titleLines: placeLines(titleBlock, titleBox),
    body: bodyBlock,
    bodyBox,
    bodyLines: bodyBlock.lines.length ? placeLines(bodyBlock, bodyBox) : [],
    empty,
  };
}

export interface ListingRow {
  address: string;
  price: string;
  /** Centre y of the row. */
  y: number;
}

export interface ListingsBoardLayout {
  heading: { text: string; box: Region; fontPx: number };
  rows: ListingRow[];
  rowFontPx: number;
  rowBox: Region;
  more: { text: string; y: number } | null;
  empty: string | null;
  footer: { text: string; box: Region; fontPx: number };
}

export const LISTINGS_HEADING = "Plots for sale";
export const LISTINGS_FOOTER = "Ask at the desk";
export const LISTINGS_EMPTY = "No plots for sale right now";

/** Board wording (the Real Estate office by default; the showrooms list catalog highlights). */
export interface ListingsCopy {
  heading: string;
  footer: string;
  empty: string;
}

export const OFFICE_LISTINGS_COPY: ListingsCopy = { heading: LISTINGS_HEADING, footer: LISTINGS_FOOTER, empty: LISTINGS_EMPTY };

/**
 * Heading, up to N rows "address .... price" and a footer. When more listings exist than rows fit,
 * the last row becomes "+N more". Long addresses are ellipsized; prices are never shortened.
 */
export function layoutListingsBoard(
  listings: readonly ListingLineText[] | null,
  measureBold: MeasureFn,
  measureRegular: MeasureFn,
  width: number,
  height: number,
  copy: ListingsCopy = OFFICE_LISTINGS_COPY,
): ListingsBoardLayout {
  const pad = Math.round(Math.min(width, height) * 0.06);
  const headingH = Math.round(height * 0.16);
  const footerH = Math.round(height * 0.1);
  const headingBox: Region = { x: pad, y: pad, w: width - pad * 2, h: headingH };
  const footerBox: Region = { x: pad, y: height - pad - footerH, w: width - pad * 2, h: footerH };
  const headingPx = Math.min(Math.floor(headingH * 0.7), fitPx(measureBold, copy.heading, headingBox.w, headingH * 0.7));
  const footerPx = Math.min(Math.floor(footerH * 0.7), fitPx(measureRegular, copy.footer, footerBox.w, footerH * 0.7));
  const rowBox: Region = { x: pad, y: headingBox.y + headingH + pad * 0.5, w: width - pad * 2, h: footerBox.y - pad * 0.5 - (headingBox.y + headingH + pad * 0.5) };
  const items = listings ?? [];
  // At most five rows so each line stays readable across the room; the rest becomes "+N more".
  const rowH = Math.max(28, Math.min(76, rowBox.h / Math.max(4, Math.min(5, items.length || 4))));
  const maxRows = Math.max(1, Math.floor(rowBox.h / rowH + 1e-6));
  const rowPx = Math.max(14, Math.floor(rowH * 0.64));
  const rows: ListingRow[] = [];
  let more: ListingsBoardLayout["more"] = null;
  const visible = items.length > maxRows ? maxRows - 1 : items.length;
  for (let i = 0; i < visible; i++) {
    const it = items[i]!;
    const y = rowBox.y + rowH * i + rowH / 2;
    const priceW = measureBold(it.priceLabel, rowPx);
    const addressMax = Math.max(40, rowBox.w - priceW - rowPx * 1.2);
    rows.push({ address: ellipsize(measureRegular, it.address, addressMax, rowPx), price: it.priceLabel, y });
  }
  if (items.length > visible) more = { text: `+${items.length - visible} more`, y: rowBox.y + rowH * visible + rowH / 2 };
  return {
    heading: { text: copy.heading, box: headingBox, fontPx: headingPx },
    rows,
    rowFontPx: rowPx,
    rowBox,
    more,
    empty: items.length === 0 ? copy.empty : null,
    footer: { text: copy.footer, box: footerBox, fontPx: footerPx },
  };
}

function fitPx(measure: MeasureFn, text: string, maxW: number, maxPx: number): number {
  let px = Math.floor(maxPx);
  while (px > 10 && measure(text, px) > maxW) px -= 1;
  return px;
}
